import type {
  ActivityAdapter,
  ActivityCompletionMetrics
} from "@/app/integration/contracts";
import { LETTER_SOUND_PRONUNCIATIONS } from "@/audio/curriculum-catalog";
import { getEnglishChoiceFeedbackCue } from "@/audio/curriculum-catalog/english-feedback-cues";
import { ACTIVITY_PAYLOAD_SCHEMAS } from "@/content/registry-core/activity-payload-schemas";
import type {
  LetterSoundPayload,
  PictureWordMatchPayload
} from "@/content/english";
import {
  advanceHintEscalation,
  type ActiveHintStage
} from "@/game/feedback";
import type { InputMethod, NavigationDirection } from "@/game/input";
import {
  createChoiceActivityState,
  reduceChoiceActivity,
  type ChoiceActivityState,
  type SingleChoicePayload
} from "@/game/templates/choice-match";

import { requireEnglishActivityAudioKey } from "./audio-bridge";

export type EnglishMediaStatus = "idle" | "playing" | "unavailable";

export interface EnglishChoicePlayerState {
  readonly activity: ChoiceActivityState;
  readonly mediaInputMethod: InputMethod | null;
  readonly mediaStatus: EnglishMediaStatus;
}

export type EnglishChoicePlayerAction =
  | {
      readonly type: "choose";
      readonly choiceId: string;
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "move-focus";
      readonly currentChoiceId: string;
      readonly direction: NavigationDirection;
    }
  | { readonly type: "activate-focused" }
  | {
      readonly type: "play-media";
      readonly inputMethod: InputMethod;
    }
  | {
      readonly type: "media-ended";
      readonly playbackRequest: number;
    }
  | {
      readonly type: "media-unavailable";
      readonly playbackRequest: number;
    }
  | { readonly type: "request-hint" }
  | { readonly type: "reset" };

export interface EnglishChoiceFeedbackRenderModel {
  readonly audioPath: string;
  readonly kind: "hint" | "retry";
  readonly playbackRequest: number;
  readonly stage: ActiveHintStage;
  readonly message: string;
}

export interface EnglishChoiceOptionRenderModel {
  readonly audioPath: string | null;
  readonly id: string;
  readonly label: string;
  readonly selected: boolean;
  readonly focused: boolean;
  readonly disabled: boolean;
}

interface EnglishChoicePromptBase {
  readonly audioKey: ReturnType<typeof requireEnglishActivityAudioKey>;
  readonly audioCaption: string;
  readonly audioInputMethod: InputMethod | null;
  readonly mediaStatus: EnglishMediaStatus;
  readonly playbackRequest: number;
}

export interface EnglishLetterSoundPromptRenderModel
  extends EnglishChoicePromptBase {
  readonly kind: "letter-sound";
  readonly exampleWord: string;
  readonly phoneme: string;
}

export interface EnglishPictureWordPromptRenderModel
  extends EnglishChoicePromptBase {
  readonly kind: "picture-word-match";
  readonly pictureDescription: string;
  readonly pictureAsset: string;
}

export type EnglishChoicePromptRenderModel =
  | EnglishLetterSoundPromptRenderModel
  | EnglishPictureWordPromptRenderModel;

export interface EnglishChoicePlayerRenderModel {
  readonly completed: boolean;
  readonly choices: readonly EnglishChoiceOptionRenderModel[];
  readonly feedback: EnglishChoiceFeedbackRenderModel | null;
  readonly prompt: EnglishChoicePromptRenderModel;
}

function toChoicePayload(
  payload: LetterSoundPayload | PictureWordMatchPayload
): SingleChoicePayload {
  return {
    kind: "single-choice",
    choices: payload.choiceTokens.map((token) => ({
      id: token,
      label: token
    })),
    correctChoiceId: payload.answerTokens[0]
  };
}

function createInitialState(
  payload: LetterSoundPayload | PictureWordMatchPayload
): EnglishChoicePlayerState {
  return {
    activity: createChoiceActivityState(toChoicePayload(payload)),
    mediaInputMethod: null,
    mediaStatus: "idle"
  };
}

function synchronizeChoiceFocus(
  payload: SingleChoicePayload,
  state: ChoiceActivityState,
  currentChoiceId: string
): ChoiceActivityState {
  const isKnownChoice = payload.choices.some(
    ({ id }) => id === currentChoiceId
  );

  if (!isKnownChoice || state.focusedChoiceId === currentChoiceId) {
    return state;
  }

  return {
    ...state,
    focusedChoiceId: currentChoiceId,
    lastInputMethod: "keyboard"
  };
}

function reduceChoicePlayer(
  state: EnglishChoicePlayerState,
  payload: LetterSoundPayload | PictureWordMatchPayload,
  action: EnglishChoicePlayerAction
): EnglishChoicePlayerState {
  const choicePayload = toChoicePayload(payload);

  switch (action.type) {
    case "reset": {
      const reset = createInitialState(payload);

      return {
        ...reset,
        activity: {
          ...reset.activity,
          promptReplayCount: state.activity.promptReplayCount
        }
      };
    }
    case "play-media":
      return {
        activity: reduceChoiceActivity(choicePayload, state.activity, {
          type: "replay-prompt"
        }),
        mediaInputMethod: action.inputMethod,
        mediaStatus: "playing"
      };
    case "media-ended":
      return state.mediaStatus === "playing" &&
        action.playbackRequest === state.activity.promptReplayCount
        ? { ...state, mediaStatus: "idle" }
        : state;
    case "media-unavailable":
      if (action.playbackRequest !== state.activity.promptReplayCount) {
        return state;
      }

      return state.mediaStatus === "unavailable"
        ? state
        : { ...state, mediaStatus: "unavailable" };
    case "request-hint":
      return {
        ...state,
        activity: {
          ...state.activity,
          feedback: null,
          hintState: advanceHintEscalation(state.activity.hintState)
        }
      };
    case "move-focus":
      return {
        ...state,
        activity: reduceChoiceActivity(
          choicePayload,
          synchronizeChoiceFocus(
            choicePayload,
            state.activity,
            action.currentChoiceId
          ),
          { type: "move-focus", direction: action.direction }
        )
      };
    case "choose": {
      const selected = reduceChoiceActivity(choicePayload, state.activity, {
        type: "select-choice",
        choiceId: action.choiceId,
        inputMethod: action.inputMethod
      });

      return {
        ...state,
        activity: reduceChoiceActivity(choicePayload, selected, {
          type: "submit"
        })
      };
    }
    case "activate-focused": {
      const selected = reduceChoiceActivity(
        choicePayload,
        state.activity,
        action
      );

      return selected === state.activity
        ? state
        : {
            ...state,
            activity: reduceChoiceActivity(choicePayload, selected, {
              type: "submit"
            })
          };
    }
  }
}

function getMetrics(
  state: EnglishChoicePlayerState
): ActivityCompletionMetrics {
  const score = state.activity.completionScore;

  return {
    completed: state.activity.status === "complete",
    score: score?.score ?? 0,
    correctRate: score?.correctRate ?? 0,
    hintCount: state.activity.hintState.hintCount,
    retryCount: state.activity.retryCount
  };
}

function createChoices(
  state: EnglishChoicePlayerState,
  payload: LetterSoundPayload | PictureWordMatchPayload
): readonly EnglishChoiceOptionRenderModel[] {
  return payload.choiceTokens.map((token) => ({
    audioPath:
      payload.kind === "letter-sound"
        ? null
        : requireChoiceAudioPath(payload, token),
    id: token,
    label: token,
    selected: state.activity.selectedChoiceIds.includes(token),
    focused: state.activity.focusedChoiceId === token,
    disabled: state.activity.status === "complete"
  }));
}

function requireChoiceAudioPath(
  payload: PictureWordMatchPayload,
  token: string
): string {
  const audioPath = payload.tokenAudio[token];

  if (audioPath === undefined) {
    throw new Error("Missing picture-word token audio for " + token);
  }

  return audioPath;
}

function createFeedback(
  state: EnglishChoicePlayerState
): EnglishChoiceFeedbackRenderModel | null {
  const feedback = state.activity.feedback;
  const stage =
    feedback?.kind === "retry"
      ? feedback.hintStage
      : state.activity.hintState.stage;

  if (
    state.activity.status === "complete" ||
    stage === "none" ||
    (feedback !== null && feedback.kind !== "retry")
  ) {
    return null;
  }

  const cue = getEnglishChoiceFeedbackCue(stage);

  return {
    audioPath: cue.audioPath,
    kind: feedback?.kind === "retry" ? "retry" : "hint",
    message: cue.text,
    playbackRequest:
      state.activity.hintState.hintCount + state.activity.retryCount,
    stage
  };
}

type ReviewedLetterSoundToken =
  keyof typeof LETTER_SOUND_PRONUNCIATIONS;

function isReviewedLetterSoundToken(
  token: string
): token is ReviewedLetterSoundToken {
  return Object.hasOwn(LETTER_SOUND_PRONUNCIATIONS, token);
}

function getLetterPronunciation(token: string): {
  readonly exampleWord: string;
  readonly ipa: string;
} {
  if (isReviewedLetterSoundToken(token)) {
    return LETTER_SOUND_PRONUNCIATIONS[token];
  }

  throw new Error("Missing reviewed pronunciation for letter " + token);
}

function toLetterSoundRenderModel(
  state: EnglishChoicePlayerState,
  payload: LetterSoundPayload
): EnglishChoicePlayerRenderModel {
  const answer = payload.answerTokens[0];
  const pronunciation = getLetterPronunciation(answer);

  if (pronunciation.exampleWord !== payload.exampleWord) {
    throw new Error("Letter-sound example does not match pronunciation data");
  }

  return {
    completed: state.activity.status === "complete",
    choices: createChoices(state, payload),
    feedback: createFeedback(state),
    prompt: {
      kind: "letter-sound",
      audioKey: requireEnglishActivityAudioKey(payload.soundAudio),
      audioCaption: "/" + pronunciation.ipa + "/",
      audioInputMethod: state.mediaInputMethod,
      exampleWord: payload.exampleWord,
      phoneme: pronunciation.ipa,
      mediaStatus: state.mediaStatus,
      playbackRequest: state.activity.promptReplayCount
    }
  };
}

const PICTURE_WORD_DESCRIPTIONS = {
  apple: "一个红苹果",
  ball: "一个彩色皮球",
  book: "一本合上的书",
  cat: "一只小猫",
  chair: "一把教室椅子",
  dog: "一只小狗"
} as const;

type ReviewedPictureWord = keyof typeof PICTURE_WORD_DESCRIPTIONS;

function isReviewedPictureWord(value: string): value is ReviewedPictureWord {
  return Object.hasOwn(PICTURE_WORD_DESCRIPTIONS, value);
}

function getPictureDescription(answer: string): string {
  if (isReviewedPictureWord(answer)) {
    return PICTURE_WORD_DESCRIPTIONS[answer];
  }

  throw new Error("Missing reviewed picture description for " + answer);
}

function toPictureWordRenderModel(
  state: EnglishChoicePlayerState,
  payload: PictureWordMatchPayload
): EnglishChoicePlayerRenderModel {
  const answer = payload.answerTokens[0];

  return {
    completed: state.activity.status === "complete",
    choices: createChoices(state, payload),
    feedback: createFeedback(state),
    prompt: {
      kind: "picture-word-match",
      audioKey: requireEnglishActivityAudioKey(payload.wordAudio),
      audioCaption: answer,
      audioInputMethod: state.mediaInputMethod,
      pictureDescription: getPictureDescription(answer),
      pictureAsset: payload.pictureAsset,
      mediaStatus: state.mediaStatus,
      playbackRequest: state.activity.promptReplayCount
    }
  };
}

export const englishLetterSoundAdapter: ActivityAdapter<
  LetterSoundPayload,
  EnglishChoicePlayerState,
  EnglishChoicePlayerAction,
  EnglishChoicePlayerRenderModel
> = {
  activityType: "letter-sound",
  parsePayload: (value) =>
    ACTIVITY_PAYLOAD_SCHEMAS.english["letter-sound"].parse(value),
  createInitialState: (payload) => createInitialState(payload),
  reduce: (state, payload, action) =>
    reduceChoicePlayer(state, payload, action),
  getMetrics: (state) => getMetrics(state),
  toRenderModel: toLetterSoundRenderModel
};

export const englishPictureWordMatchAdapter: ActivityAdapter<
  PictureWordMatchPayload,
  EnglishChoicePlayerState,
  EnglishChoicePlayerAction,
  EnglishChoicePlayerRenderModel
> = {
  activityType: "picture-word-match",
  parsePayload: (value) =>
    ACTIVITY_PAYLOAD_SCHEMAS.english["picture-word-match"].parse(value),
  createInitialState: (payload) => createInitialState(payload),
  reduce: (state, payload, action) =>
    reduceChoicePlayer(state, payload, action),
  getMetrics: (state) => getMetrics(state),
  toRenderModel: toPictureWordRenderModel
};

export const englishChoicePlayerActions = {
  requestHint: (): EnglishChoicePlayerAction => ({ type: "request-hint" }),
  replay: (): EnglishChoicePlayerAction => ({ type: "reset" })
};
